import { FC, useState } from "react";
import { ChatState } from "../../../types";
import { Chat, ChatStatus, Color, Vec2 } from "../../../../../types";
import { Column, Row } from "../../../../../components/containers";
import { AlignItems } from "../../../../../components/containers/types";
import {
  useChatStore,
  usePopupStore,
  useSocketStore,
  useThemeStore,
} from "../../../../../stores";
import { Text } from "../../../../../components/typography";
import { getTimeStr } from "../../../../../utils";
import ChatFile from "../chat-file";
import OptionList from "../../../../../components/optionlist";
import { EditIcon, TrashIcon } from "../../../../../assets/icons";
import { QuestionBox } from "../../../../../popup";
import {
  CHAT_PAGE_ROOM_IDX,
  SIDE_PANEL_ROOM_IDX,
} from "../../../../../constants";
import { OptionListOption } from "../../../../../components/optionlist/types";

const DEFAULT_CHAT_STATUS = ChatStatus.Sent;

const MyChat: FC<ChatState & { chat: Chat }> = ({
  chat,
  isSidePanel,
  setEditingChat,
}) => {
  const theme = useThemeStore((s) => s.theme);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const sockDispatch = useSocketStore((s) => s.sockDispatch);
  const selectedRooms = useChatStore((s) => s.selectedRooms);

  const [mousePos, setMousePos] = useState<Vec2>({ x: 0, y: 0 });

  const getTextBg = (): Color => {
    switch (chat.status || DEFAULT_CHAT_STATUS) {
      case ChatStatus.Sent:
        return theme.chat.primary;
      case ChatStatus.Waiting:
        return theme.icon;
      case ChatStatus.Failed:
        return theme.alert;
      default:
        return "black";
    }
  };

  const getTextColor = (): Color => {
    switch (chat.status || DEFAULT_CHAT_STATUS) {
      case ChatStatus.Sent:
        return theme.chat.textOnPrimary;
      case ChatStatus.Waiting:
        return "white";
      case ChatStatus.Failed:
        return theme.textOnAlert;
      default:
        return "black";
    }
  };

  const onRightClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    e.preventDefault();
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const onEdit = (): void => {
    if (setEditingChat) setEditingChat(chat);
  };

  const onDelete = (): void => {
    setPopupBody(
      <QuestionBox
        question="آیا مطمئن هستید که می خواهید این پیام را حذف کنید؟"
        onYes={() => {
          sockDispatch("delete_message", {
            id: chat._id.$oid,
            chat:
              selectedRooms[
                isSidePanel ? SIDE_PANEL_ROOM_IDX : CHAT_PAGE_ROOM_IDX
              ]?._id.$oid || "",
          });
        }}
      />
    );
  };

  const getOptions = (): OptionListOption[] => {
    if (chat.status !== ChatStatus.Waiting)
      return [
        {
          title: "ویرایش",
          Icon: EditIcon,
          onClick: onEdit,
        },
        {
          title: "حذف",
          Icon: TrashIcon,
          color: theme.alert,
          iconColor: theme.alert,
          onClick: onDelete,
        },
      ];
    return [];
  };

  return (
    <Column
      style={{ maxWidth: "70%", margin: "1rem 8px" }}
      gap={4}
      alignItems={AlignItems.Start}
      props={{ onContextMenu: onRightClick }}
      className="chat"
    >
      <OptionList mousePos={mousePos} options={[...getOptions()]} />

      {chat.files.map((file) => (
        <ChatFile
          key={!file.local ? file.filename : file.name}
          file={file}
          status={chat.status || DEFAULT_CHAT_STATUS}
          isMe
          isSidePanel={isSidePanel}
        />
      ))}

      {chat.content ? (
        <Row
          background={getTextBg()}
          style={{
            maxWidth: "calc(100% - 16px)",
            padding: "8px",
            borderRadius: "15px 0 15px 15px",
          }}
        >
          <Text
            textStyles={{
              whiteSpace: "pre-line",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            color={getTextColor()}
            size={13}
          >
            {chat.content}
          </Text>
        </Row>
      ) : (
        <></>
      )}

      <Text color={theme.icon}>{getTimeStr(chat.date)}</Text>
    </Column>
  );
};

export default MyChat;
